// The Hub overview: the hub as a business, in three tabs and no docked panel.
// Summary has the figures, earnings by question and how the market treats the hub; About has what
// the hub covers and its knowledge sources, which open in place; Needs attention pins the hub's
// WarmHub access when it needs a grant and ranks the rest by money. The questions themselves live on the Questions page. A tab is in
// the URL (#/<hub>/overview/<tab>). See Design > UI, "Tabs" and "Workspace".

import { useEffect } from "preact/hooks";
import { Button, countLabel, EmptyState, FigureStrip, fmtNum, Icon, LineChart, PageBar, SampleMark, StatusBadge, Tabs, Workspace } from "../../../../ui/src/index.ts";
import type { ChartTone, TabChange } from "../../../../ui/src/index.ts";
import { attention, hubOutcomes, isLive } from "../../domain/activity.ts";
import { percent, usd, usdFromNano } from "../../domain/money.ts";
import { netMargin } from "../../domain/sample.ts";
import type { EventRow, HubSummary } from "../../domain/types.ts";
import { loadEvents, refresh } from "../actions.ts";
import { HeaderActions } from "../Guide.tsx";
import { HubSummaryPanel, intentLabel, SOURCE_TONE, sourcesFor, type HubData } from "../panels.tsx";
import { Failed, Loading, Section } from "../parts.tsx";
import { href, navigate, OVERVIEW_TABS, type OverviewTab } from "../router.ts";
import { toolsetInUse } from "../../domain/toolsets.ts";
import { capitalize, day, shortCommit } from "../words.ts";
import { useHubData } from "../hubData.ts";
import { useStore } from "../store.ts";
import { accessItem, attentionItems, itemHref, type Item } from "../attention.ts";

const TONES: ChartTone[] = ["info", "cyan", "violet"];
/** Revenue per day for the top three questions and the rest, from the newest events. */
function earningsSeries(d: HubData, events: ReadonlyArray<EventRow>) {
  const answered = events.filter((e) => !e.test_run && e.outcome === "Answer" && e.intent && Number.isFinite(Number(e.detail?.price)));
  if (answered.length === 0) return null;
  const dayOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const days = [...new Set(answered.map((e) => dayOf(e.created_at)))].sort();
  const top = [...d.activity.values()].filter((a) => a.revenueUsd > 0).sort((a, b) => b.revenueUsd - a.revenueUsd).slice(0, 3).map((a) => a.intent);
  const series = [...top, "other"].map((id) => ({ id, values: days.map(() => 0), total: 0 }));
  for (const e of answered) {
    const s = series.find((x) => x.id === e.intent) ?? series.at(-1)!;
    const v = Number(e.detail?.price) / 1e6;
    s.values[days.indexOf(dayOf(e.created_at))]! += v;
    s.total += v;
  }
  const axis: [string, string] = [day(Date.parse(days[0]!)), day(Date.parse(days.at(-1)!))];
  return {
    axis,
    series: series.filter((s) => s.total > 0).map((s, i) => ({ name: s.id === "other" ? "All other questions" : intentLabel(d.hub, s.id), values: s.values, tone: s.id === "other" ? "neutral" as const : TONES[i]!, total: usd(s.total) })),
  };
}

export function Overview({ hub, tab }: { hub: HubSummary; tab: OverviewTab }) {
  const { d, events } = useHubData(hub);
  const stats = hub.stats;
  const bar = <PageBar crumbs={<span class="st-id">{d.short}</span>} title="Hub overview">
    <Button onClick={() => void refresh(hub.hub.id, 30)}><Icon name="refresh" size={16} />Refresh</Button>
    <HeaderActions />
  </PageBar>;
  // A key move between tabs replaces the history entry, so Back leaves the page instead of stepping through tabs.
  const go = (t: string, how: TabChange) => {
    const next = OVERVIEW_TABS.find((x) => x === t);
    navigate(next ? { name: "overview", hub: d.hub.hub.id, tab: next } : { name: "overview", hub: d.hub.hub.id }, how === "key");
  };
  // The overview walkthrough's stops are on Summary, so Summary stays open while it runs.
  const touring = useStore((s) => s.tour?.id === "overview");
  useEffect(() => {
    if (touring && tab !== "summary") navigate({ name: "overview", hub: d.hub.hub.id }, true);
  }, [touring, tab]);

  const attentionList = (list: Item[]) => <ul class="st-attn">{list.map((it) => <li key={it.key}>
    <div class="st-attn__text"><strong>{it.title}</strong>{it.why && <span>{it.why}</span>}</div>
    {it.moneyLabel && <span class="st-attn__money">{it.moneyLabel}</span>}
    <Button size="sm" href={itemHref(d.hub.hub.id, it)}>{it.verb}</Button>
  </li>)}</ul>;
  // The hub's WarmHub access needs no asks, so it shows on a hub that has none yet.
  const keepAnswering = (list: Item[], tour: { tour?: string } = {}) => list.length > 0 && <Section title="Keep the hub answering" note="The hub's access to WarmHub, which you grant on the Toolsets page." {...tour}>{attentionList(list)}</Section>;

  if (!stats) {
    const access = accessItem(d);
    return <>{bar}<Workspace>{keepAnswering(access ? [access] : [])}<HubAbout d={d} /><EmptyState>No asks have reached this hub yet.</EmptyState></Workspace></>;
  }

  const outcomes = hubOutcomes(stats.outcomes);
  const revenueUsd = usdFromNano(stats.revenueNano);
  const margin = netMargin(d.sample, revenueUsd, stats.answers);
  const pending = d.sample.pendingShare;
  const items = attentionItems(d, attention(stats, d.activity, d.demand?.declined));
  const list = events?.status === "ok" ? events.value.events : [];
  const earnings = earningsSeries(d, list);
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  const waiting = !events || events.status === "loading";
  const failed = events?.status === "failed" ? events : null;

  const summary = <>
    <div data-tour="summary">
      <FigureStrip label="Earnings, answers, margin and credits back" figures={[
        { tone: "accent", label: <><Icon name="earnings" size={16} />Earnings</>, value: usd(revenueUsd), context: pending !== undefined ? <>{usd(revenueUsd * pending)} pending · {usd(revenueUsd * (1 - pending))} available<SampleMark /></> : `${fmtNum(stats.answers)} answers · simulated credits`, action: { label: "By question", onSelect: () => scrollTo("st-earnings") } },
        { label: <><Icon name="questions" size={16} />Answers</>, value: fmtNum(stats.answers), context: `of ${fmtNum(outcomes.received)} asks the hub received`, action: { label: "Questions", href: href({ name: "questions", hub: d.hub.hub.id }) } },
        { label: <><Icon name="pricing" size={16} />Net margin<SampleMark /></>, value: margin === null ? "–" : percent(margin, 1), context: "after delivery fees and inputs", action: { label: "Pricing signals", href: href({ name: "pricing", hub: d.hub.hub.id }) } },
        { label: <><Icon name="reputation" size={16} />Credits back</>, value: percent(stats.refunds, stats.answers), context: `${fmtNum(stats.refunds)} of ${fmtNum(stats.answers)} answers`, action: { label: "Reputation", href: href({ name: "reputation", hub: d.hub.hub.id }) } },
      ]} />
    </div>
    <section class="st-section" id="st-earnings">
        <div class="st-section__head"><div><h2>Earnings by question</h2><p class="st-note">Per day{list.length > 0 ? `, from the last ${fmtNum(list.length)} events` : ""}. The three that earn most, and the rest.</p></div></div>
        {waiting ? <Loading label="Loading events" />
          : failed ? <Failed what="the events" error={failed.error} retry={() => void loadEvents(hub.hub.id, true)} />
          : earnings ? <LineChart label="Earnings per day by question" series={earnings.series} axis={earnings.axis} />
          : <EmptyState>No answers in the newest events.</EmptyState>}
    </section>
    <section class="st-section" data-tour="hub-summary">
      <div class="st-section__head"><div><h2>How the market treats this hub</h2><p class="st-note">Where its asks went, how they were answered and how consumers paid.</p></div></div>
      <div class="st-overview-summary"><HubSummaryPanel d={d} inline /></div>
    </section>
  </>;

  // The hub's WarmHub access leads; then items with money at stake, in the order they were ranked; the rest are worth a look.
  const pinned = items.filter((it) => it.pinned);
  const money = items.filter((it) => !it.pinned && (it.money ?? 0) > 0);
  const rest = items.filter((it) => !it.pinned && !((it.money ?? 0) > 0));
  const stake = money.reduce((total, it) => total + (it.money ?? 0), 0);
  // The walkthrough points at the first section shown.
  const tourAt = (list: Item[]) => ([pinned, money, rest].find((l) => l.length > 0) === list ? { tour: "attention" } : {});
  const attentionTab = items.length === 0 ? <Section title="Needs attention" tour="attention"><EmptyState>Nothing needs attention.</EmptyState></Section> : <>
    {keepAnswering(pinned, tourAt(pinned))}
    {money.length > 0 && <Section title="Money at stake" note={`About ${usd(stake)} across ${countLabel(money.length, "item")}, largest first. Each one opens the page that deals with it.`} {...tourAt(money)}>{attentionList(money)}</Section>}
    {rest.length > 0 && <Section title="Worth a look" note="No amount to count, but each shapes how the hub is read." {...tourAt(rest)}>{attentionList(rest)}</Section>}
  </>;

  return <>
    {bar}
    <Workspace>
      <Tabs label="Hub overview sections" value={tab} onChange={go} items={[
        { id: "summary", label: "Summary", content: summary },
        { id: "about", label: "About", content: <HubAbout d={d} /> },
        { id: "attention", label: "Needs attention", count: items.length, content: attentionTab },
      ]} />
    </Workspace>
  </>;
}

/** What the hub covers and commits to, and the knowledge its answers come from. A source opens in place. */
export function HubAbout({ d }: { d: HubData }) {
  const live = d.hub.manifest.intents.filter(isLive).length;
  const inUse = toolsetInUse(d.hub);
  return <>
    <section class="st-section">
      <div class="st-section__head"><div><h2>What it covers</h2><p class="st-note">Its scope, and what it commits to answer.</p></div></div>
      <dl class="st-about">
        <div><dt>Covers</dt><dd>{d.hub.manifest.scope}</dd></div>
        {d.hub.manifest.notInScope && <div><dt>Doesn't cover</dt><dd>{d.hub.manifest.notInScope}</dd></div>}
        <div><dt>Commits to</dt><dd>{countLabel(live, "question")}{d.hub.manifest.intents.length > live ? `, with ${d.hub.manifest.intents.length - live} planned` : ""}{inUse && <span class="st-note"> · toolset <span class="st-id">{shortCommit(inUse.commit)}</span> {inUse.status} since {day(inUse.createdAt)}</span>}</dd></div>
      </dl>
    </section>
    <section class="st-section">
      <div class="st-section__head"><div><h2>Knowledge sources</h2><p class="st-note">What the hub reads to answer. Open one to see its health and which questions read it.</p></div></div>
      <SourceRows d={d} />
    </section>
  </>;
}

/** Each knowledge source as a row that opens in place: its health or freshness, cost, and the questions that read it. */
function SourceRows({ d }: { d: HubData }) {
  if (d.sources.length === 0) return <EmptyState>The hub's toolset doesn't list any knowledge sources yet.</EmptyState>;
  const live = d.hub.manifest.intents.filter(isLive);
  return <ul class="st-sources-list">{d.sources.map((s) => {
    const readers = live.filter((i) => sourcesFor(d, i.id).some((x) => x.name === s.name));
    return <li key={s.name}><details>
      <summary><span class="st-q">{s.name}</span><span class="st-note">{s.type}</span><span class="st-note">{s.readBy ? `${s.readBy.length} of ${live.length}` : "All"} questions</span>{s.sample && <SampleMark />}<StatusBadge tone={SOURCE_TONE(s.state)}>{s.state}</StatusBadge><Icon name="chevron" size={16} class="st-sources-list__chevron" /></summary>
      <div class="st-sources-list__body">
        <dl class="st-facts"><div><dt>{s.type === "API" ? "Health" : "Freshness"}</dt><dd>{s.detail}</dd></div>{s.cost && <div><dt>Cost</dt><dd>{s.cost}</dd></div>}</dl>
        {readers.length > 0 && <div><p class="st-caption">Questions that read it</p><ul class="st-plain">{readers.map((i) => <li key={i.id}><a href={href({ name: "question", hub: d.hub.hub.id, intent: i.id })}>{capitalize(i.label)}</a></li>)}</ul></div>}
        {(s.state === "Degraded" || s.state === "Stale") && <p class="st-note">When a source degrades, answers that read it can go wrong, and credits back follow. Fixing the source is usually faster than repricing.</p>}
      </div>
    </details></li>;
  })}</ul>;
}
